import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, RefreshCw, ArrowRight } from "lucide-react";

/**
 * Live exchange rates banner — fetches the rates used by the production engine
 * via /api/quote so the displayed values stay in sync with what clients actually pay.
 */
export default function ExchangeRates() {
  const [rates, setRates] = useState({ tg_ga: null, ga_fr: null });
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [tgGa, gaFr] = await Promise.all([
        api.get("/quote", { params: { amount_xof: 10000, corridor: "tg_ga" } }),
        api.get("/quote", { params: { amount_xof: 10000, corridor: "ga_fr" } }),
      ]);
      setRates({ tg_ga: tgGa.data, ga_fr: gaFr.data });
      setRefreshedAt(new Date());
    } catch (_e) { /* ignore network errors here */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const xofToXaf = rates.tg_ga?.exchange_rate ?? 1;
  const xafToEur = rates.ga_fr?.exchange_rate ?? 0;
  const eurToXaf = xafToEur > 0 ? Math.round(1 / xafToEur) : 0;

  return (
    <section className="py-20 bg-[var(--sl-bg-alt)]" data-testid="exchange-rates-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)] flex items-center gap-2">
              <TrendingUp size={14} /> Cours des devises
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">Taux appliqués aujourd'hui.</h2>
            <p className="mt-2 text-gray-600 text-sm sm:text-base max-w-xl">
              Aucune marge cachée : ce sont exactement les taux utilisés par notre moteur de calcul lors de chaque transfert.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-white border border-gray-200 px-4 py-2 text-xs font-medium text-gray-700 hover:border-[var(--sl-primary)] hover:text-[var(--sl-primary)] transition shadow-sm disabled:opacity-60"
            data-testid="exchange-refresh"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            {refreshedAt ? `Actualisé · ${refreshedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "Charger"}
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
          <RateCard
            from="XOF"
            fromLabel="Franc CFA (BCEAO)"
            to="XAF"
            toLabel="Franc CFA (BEAC)"
            value={xofToXaf}
            display={`1 XOF = ${xofToXaf.toLocaleString("fr-FR", { maximumFractionDigits: 4 })} XAF`}
            flagFrom="🇹🇬"
            flagTo="🇬🇦"
          />
          <RateCard
            from="XAF"
            fromLabel="Franc CFA (BEAC)"
            to="EUR"
            toLabel="Euro"
            value={xafToEur}
            display={xafToEur > 0 ? `1 XAF = ${xafToEur.toFixed(6)} EUR` : "—"}
            flagFrom="🇬🇦"
            flagTo="🇫🇷"
          />
          <RateCard
            from="EUR"
            fromLabel="Euro"
            to="XAF"
            toLabel="Franc CFA (BEAC)"
            value={eurToXaf}
            display={eurToXaf > 0 ? `1 EUR ≈ ${eurToXaf.toLocaleString("fr-FR")} XAF` : "—"}
            flagFrom="🇫🇷"
            flagTo="🇬🇦"
            highlight
          />
        </div>

        <p className="text-xs text-gray-500 mt-6 max-w-2xl">
          Les taux peuvent évoluer en fonction des marchés. Le taux applicable à votre transfert est celui affiché lors de la confirmation.
        </p>
      </div>
    </section>
  );
}

function RateCard({ from, fromLabel, to, toLabel, display, flagFrom, flagTo, highlight }) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-white border p-5 sm:p-6 transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-15px_rgba(10,77,60,0.25)] ${
        highlight ? "border-[var(--sl-secondary)]/40" : "border-gray-200"
      }`}
      data-testid={`rate-card-${from}-${to}`}
    >
      {highlight && (
        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-[var(--sl-secondary-dark)] font-semibold bg-[var(--sl-secondary)]/15 rounded-full px-2 py-0.5">
          le plus consulté
        </span>
      )}
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 font-semibold">
        <span className="text-base leading-none">{flagFrom}</span>
        <span>{from}</span>
        <ArrowRight size={12} className="text-gray-400" />
        <span>{to}</span>
        <span className="text-base leading-none">{flagTo}</span>
      </div>
      <div className="font-heading text-xl sm:text-2xl font-bold mt-4 text-gray-900 tabular-nums">
        {display}
      </div>
      <div className="text-xs text-gray-500 mt-2 leading-snug">
        <span className="font-medium text-gray-700">{fromLabel}</span> → <span className="font-medium text-gray-700">{toLabel}</span>
      </div>
    </div>
  );
}
